'use client';

import Image from 'next/image';

import { AccessibleHiddenText } from '@ria-client/tw-shared-ui';
import { ADVISORS_MATCH_BASE_URL } from '@ria-client/utils/advisorMatch/urls';
import { useAdUrl } from '../lib/use-ad-url';
import type { NodeProps } from '../types';
import { ContentLink } from './lib/content-link';

export interface AnnouncementBannerProps extends Record<string, unknown> {
  boldText?: string;
  text?: string;
  buttonUrl?: string;
}

const DEFAULT_PROPS = {
  boldText: 'Find the perfect financial advisor for your money goals',
  text: 'through NerdWallet Advisor Match.',
  buttonUrl: `${ADVISORS_MATCH_BASE_URL}/currently-has-advisor`,
};

const BANNER_PARAMS = { utm_content: 'banner' };

export function AnnouncementBanner({ props }: NodeProps<AnnouncementBannerProps>) {
  const {
    boldText = DEFAULT_PROPS.boldText,
    text = DEFAULT_PROPS.text,
    buttonUrl = DEFAULT_PROPS.buttonUrl,
  } = props;
  const href = useAdUrl(buttonUrl, BANNER_PARAMS);

  return (
    <aside
      aria-label="NerdWallet Advisors announcement banner"
      className="flex flex-col items-start gap-4 rounded bg-[var(--content-blue-extra-light)] px-8 py-6 md:flex-row md:items-center"
    >
      <Image
        alt="sparkle-illustration"
        height={19}
        src="https://cdns3.nerdwallet.com/bain-components/tax-announcement-banner/announcement-sparkle.svg"
        width={24}
      />
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:gap-0">
        <div className="pr-4 text-[var(--content-blue-dark)]">
          <span className="typography-text-small font-bold">{boldText}</span>
          <span className="typography-text-small">{text}</span>
        </div>
        <div className="flex w-full items-center justify-between md:w-auto md:gap-4 md:px-6">
          <span className="typography-text-small whitespace-nowrap font-medium">
            <ContentLink
              aria-label="Learn more about NerdWallet Advisors"
              className="text-secondary-foreground"
              href={href}
            >
              LEARN MORE
              <AccessibleHiddenText as="span">about NerdWallet Advisors</AccessibleHiddenText>
            </ContentLink>
          </span>
          {/* 10px: legacy AnnouncementBanner AD label; no matching Tailwind size. */}
          <span className="typography-text-extra-small rounded border border-current px-6 py-2 text-[10px] leading-3">
            AD
          </span>
        </div>
      </div>
    </aside>
  );
}
